import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  FornecimentoDireto,
  LancamentosRejeitados,
  LancamentosRejeitadosFornecimento,
  RejeitadosReceitaFederal,
  RejeitadosReceitaFederalFornecimento,
  VinculoUsuarioOperadora,
} from '@gestor-web/@core/api/ords/model';
import { LayoutActions, OperadorasActions } from '@gestor-web/@core/store/actions';
import { LayoutSelectors, OperadorasSelectors } from '@gestor-web/@core/store/selectors';
import { filterToQueryString, pseudonimizar } from '@gestor-web/@core/utils';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { CompetenciaPipe } from '@gestor-web/app/shared/pipes/competencia.pipe';
import { CpfCnpjPipe } from '@gestor-web/app/shared/pipes/cpf-ou-cnpj.pipe';
import { GeralService, NotificationService, PermissaoService } from '@gestor-web/app/shared/services';
import { GridService } from '@gestor-web/app/shared/services/grid.service';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi } from 'ag-grid-community';
import { FUNCIONALIDADE } from '@gestor-web/@core/auth/funcionalidades';
type AgGridCommon<TData, TContext> = { api: GridApi<TData>; context: TContext };
import { GridOptions } from 'ag-grid-enterprise';
import { AgFilterUtils, buildGridOptions, exportGridAsExcel } from '@gestor-web/app/shared/components';
import { Observable, Subject, combineLatest, forkJoin } from 'rxjs';
import { filter, first, map, takeUntil } from 'rxjs/operators';

import { RejeitadosReceitaFederalActionCellRenderer } from './rejeitados-receita-federal-cell.component';
import { RejeitadosReceitaFederalService } from './rejeitados-receita-federal.service';
import { GuiaMonitorActionCellRenderer } from '@gestor-web/app/pages/private/criticas/criticas-geradas/guia-monitor-action-cell.component';
import { FornecimentoDiretoActionCellRenderer } from '@gestor-web/app/pages/private/outras-remuneracoes/fornecimento-direto/components/fornecimento-direto-action-cell.component';

const DIALOG_PADDING_MAX_OFF = 100;
const DIALOG_PADDING_MAX_ON = 35;

const GRID_KEY = 'ANALISES.REJEITADOS_RECEITA_FEDERAL';
const GRID_DIALOG_KEY = 'ANALISES.REJEITADOS_RECEITA_FEDERAL.LANCAMENTOS';

const COLUMNS: ColDef<Row>[] = [
  {
    headerName: '003-Competência',
    field: 'nr_competencia',
    type: 'competencia',
    width: 170,
  },
  {
    headerName: '016-CNS - Cartão Nacional de Saúde',
    field: 'nr_cns',
    width: 170,
  },
  {
    headerName: '121-CPF do beneficiário',
    field: 'nr_cpf_beneficiario',
    type: 'cpfCnpj',
    width: 150,
  },
  {
    headerName: '017-Sexo do beneficiário',
    field: 'nm_id_sexo',
    width: 150,
  },
  {
    headerName: '018-Data nascimento',
    field: 'dt_nascimento',
    type: 'date',
    width: 150,
  },
  {
    headerName: 'Qtde. lançamentos criticados',
    field: 'qt_lancto',
    width: 180,
    cellRenderer: RejeitadosReceitaFederalActionCellRenderer,
  },
];

const COLUMNS_FORNECIMENTO: ColDef<RowFornecimento>[] = [
  {
    headerName: '003-Competência',
    field: 'nr_competencia',
    type: 'competencia',
  },
  {
    headerName: '096-CNS - Cartão nacional de saúde',
    field: 'nr_cns',
  },
  {
    headerName: '126-CPF do beneficiário',
    field: 'nr_cpf_beneficiario',
    type: 'cpfCnpj',
  },
  {
    headerName: '097-Sexo do beneficiário',
    field: 'nm_id_sexo',
  },
  {
    headerName: '098-Data de nascimento',
    field: 'dt_nascimento',
    type: 'date',
  },
  {
    headerName: 'Qtde. lançamentos criticados',
    field: 'qt_lancto',
    cellRenderer: RejeitadosReceitaFederalActionCellRenderer,
  },
];

const COLUMNS_LANCAMENTOS: ColDef[] = [
  {
    headerName: '003-Competência',
    field: 'nr_competencia',
    type: 'competencia',
  },
  {
    headerName: 'Cód. lancto',
    field: 'cd_guia_monitor',
    cellRenderer: GuiaMonitorActionCellRenderer,
  },
  {
    headerName: '012-CNES executante',
    field: 'nr_cnes_exec',
  },
  {
    headerName: '014-CNPJ/CPF executante',
    field: 'nr_cnpj_cpf_exec',
    type: 'cpfCnpj',
  },
  {
    headerName: '023-Nr. guia no prestador',
    field: 'nr_guia_prestador',
  },
  {
    headerName: '024-Nr. guia na operadora',
    field: 'nr_guia_operadora',
  },
  {
    headerName: '025-Nr. reembolso ou pagto eventual',
    field: 'nr_reembolso',
  },
  {
    headerName: '080-Data processamento',
    field: 'dt_processamento',
    type: 'date',
  },
];

const COLUMNS_LANCAMENTOS_FORNECIMENTO: ColDef[] = [
  {
    headerName: '003-Competência',
    field: 'nr_competencia',
    type: 'competencia',
  },
  {
    headerName: 'Cód. lancto',
    field: 'cd_guia_monitor',
    cellRenderer: FornecimentoDiretoActionCellRenderer,
  },
  {
    headerName: '101-Identificador da operação de fornecimento de materiais e medicamentos',
    field: 'nr_fornecimento',
    width: 280,
  },
  {
    headerName: '126-CPF do beneficiário',
    field: 'nr_cpf_beneficiario',
    type: 'cpfCnpj',
  },
  {
    headerName: '096-CNS - Cartão nacional de saúde',
    field: 'nr_cns',
  },
  {
    headerName: '097-Sexo do beneficiário',
    field: 'nm_id_sexo',
  },
  {
    headerName: '098-Data nascimento',
    field: 'dt_nascimento',
    type: 'date',
  },
  {
    headerName: 'Motivo rejeição',
    field: 'ds_motivo_glosa',
    width: 240,
  },
];

@Component({
    templateUrl: './rejeitados-receita-federal.component.html',
    styleUrls: ['./rejeitados-receita-federal.component.scss'],
    providers: [RejeitadosReceitaFederalService, DecimalPipe],
    standalone: false
})
export class RejeitadosReceitaFederalComponent implements OnInit, OnDestroy {
  readonly GRID_KEY = GRID_KEY;
  readonly GRID_DIALOG_KEY = GRID_DIALOG_KEY;

  @ViewChild('agGrid') agGrid: AgGridAngular<Row>;
  @ViewChild('agGridFornecimento') agGridFornecimento: AgGridAngular<RowFornecimento>;
  @ViewChild('agGridDialog') agGridDialog: AgGridAngular<RowLancamentos>;
  @ViewChild('agGridDialogFornecimento') agGridDialogFornecimento: AgGridAngular<RowLancamentosFornecimento>;

  gridOptions: GridOptions<Row>;
  gridOptionsFornecimento: GridOptions<RowFornecimento>;
  gridDialogOptions: GridOptions<RowLancamentos>;
  gridDialogFornecimentoOptions: GridOptions<RowLancamentosFornecimento>;

  pageReady$ = new Subject<void>();
  filterReady$ = new Subject<boolean>();
  firstLoad = true;
  filterChanged = true;

  podeGerenciarVisoes = false;

  operadora: string;
  operadoraNome: string;
  competenciaRange: string[];

  tabIndex: number = 0;

  rowDataFornecimento: RowFornecimento[];
  rowDataFornecimentoOriginal: RowFornecimento[];

  rowData: Row[];
  rowDataOriginal: Row[];

  pseudonimizarAction: boolean = true;
  pseudonimizarActionVisible: boolean = true;

  selectedRow: Row;
  selectedRowIndex: number;

  selectedRowFornecimentoDireto: RowFornecimentoDireto;
  selectedRowIndexFornecimentoDireto: number;
  detalheGuiaCodigoFornecimento: string;
  rowFornecimentoDireto: RowFornecimentoDireto[] = [];
  dialogDisplayFornecimentoDireto: boolean = false;

  dialogDisplay = false;
  dialogDisplayFornecimento = false;
  dialogData: RowLancamentos[];
  dialogDataFornecimento: RowLancamentosFornecimento[];
  dialogSubtitle = '';

  dialogHeightPadding = DIALOG_PADDING_MAX_OFF;

  detalheGuiaDisplay = false;
  detalheGuiaCodigo: string;

  pageTooltip = GRID_KEY;

  fullscreen$: Observable<boolean>;
  gridHeight$: Observable<string>;

  constructor(
    private permissaoService: PermissaoService,
    private service: RejeitadosReceitaFederalService,
    private decimalPipe: DecimalPipe,
    private competenciaPipe: CompetenciaPipe,
    private cpfCnpjPipe: CpfCnpjPipe,
    private notificationService: NotificationService,
    private gridService: GridService,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
    private geralService: GeralService,
    private datePipe: DatePipe,
  ) {
    this.fullscreen$ = this.store.select(LayoutSelectors.selectFullscreen);
    this.gridHeight$ = this.store
      .select(LayoutSelectors.selectFullscreen)
      .pipe(map(fullscreen => (fullscreen ? 'calc(100vh - 110px)' : 'calc(100vh - 180px)')));
  }

  ngOnInit(): void {
    const ano = new Date().getFullYear();
    this.competenciaRange = [`${ano}01`, `${ano}12`];

    combineLatest([
      this.permissaoService.checkAcessoFeature(FUNCIONALIDADE.DESLIGAR_PSEUDONIMIZACAO),
      this.store.select(OperadorasSelectors.selectWorkspaceOperadora),
    ])
      .pipe(
        filter(op => !!op),
        concatLatestFrom(() => this.store.select(OperadorasSelectors.selectWorkspaceParams)),
        takeUntil(this.ngDestroyed$),
      )
      .subscribe(([[podeDesligarPseudonimizacao, operadora], params]) => {
        this.operadora = operadora.cd_registro_ans;
        this.operadoraNome = operadora.sg_operadora;
        this.competenciaRange = [
          params.competencia ? '' + params.competencia : null,
          params.competencia_final ? '' + params.competencia_final : null,
        ];

        if (operadora.ib_dado_sensivel_pseudonimizado) {
          this.pseudonimizarActionVisible = false;
        } else {
          const vinculo = operadora as VinculoUsuarioOperadora;
          this.pseudonimizarActionVisible = podeDesligarPseudonimizacao || !vinculo.ib_dado_sensivel_pseudonimizado_usuario;
        }

        this.configureGrid();

        setTimeout(() => this.pageReady$.next());
      });

    combineLatest([this.route.queryParams, this.pageReady$])
      .pipe(first())
      .subscribe(([params, _]) => this.applyQueryFilters(params));

    combineLatest([this.filterReady$, this.pageReady$])
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(([hasFilter, _]) => {
        if (!this.firstLoad || hasFilter) {
          this.reloadData();
        }
      });

    this.permissaoService
      .checkAcessoFeature(FUNCIONALIDADE.GERENCIAR_VISOES)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(podeGerenciarVisoes => {
        this.podeGerenciarVisoes = podeGerenciarVisoes;
        this.configureGrid();
      });
  }

  private configureGrid() {
    this.gridOptions = buildGridOptions<Row>(
      {
        columnDefs: COLUMNS,
        pipes: {
          decimal: this.decimalPipe,
          competencia: this.competenciaPipe,
          cpfCnpj: this.cpfCnpjPipe,
          date: this.datePipe,
        },
        views: {
          gridKey: GRID_KEY,
          hide: !this.podeGerenciarVisoes,
        },
        exportExcelFn: () => this.exportExcel(),
      },
      {
        context: {
          parentComponent: this,
        },
      },
    );

    this.gridOptionsFornecimento = buildGridOptions<RowFornecimento>(
      {
        columnDefs: COLUMNS_FORNECIMENTO,
        pipes: {
          decimal: this.decimalPipe,
          competencia: this.competenciaPipe,
          cpfCnpj: this.cpfCnpjPipe,
          date: this.datePipe,
        },
        views: {
          gridKey: GRID_KEY,
          hide: !this.podeGerenciarVisoes,
        },
        exportExcelFn: () => this.exportExcel(),
      },
      {
        context: {
          parentComponent: this,
        },
      },
    );

    this.gridDialogOptions = buildGridOptions<RowLancamentos>(
      {
        columnDefs: COLUMNS_LANCAMENTOS,
        pipes: {
          decimal: this.decimalPipe,
          competencia: this.competenciaPipe,
          cpfCnpj: this.cpfCnpjPipe,
          date: this.datePipe,
        },
        views: {
          gridKey: GRID_DIALOG_KEY,
          hide: !this.podeGerenciarVisoes,
        },
        exportExcelFn: () => this.exportExcelDialog(),
      },
      {
        context: {
          parentComponent: this,
        },
      },
    );

    this.gridDialogFornecimentoOptions = buildGridOptions<RowLancamentosFornecimento>(
      {
        columnDefs: COLUMNS_LANCAMENTOS_FORNECIMENTO,
        pipes: {
          decimal: this.decimalPipe,
          competencia: this.competenciaPipe,
          cpfCnpj: this.cpfCnpjPipe,
          date: this.datePipe,
        },
        views: {
          gridKey: GRID_DIALOG_KEY,
          hide: !this.podeGerenciarVisoes,
        },
        exportExcelFn: () => this.exportExcelDialog(),
      },
      {
        context: {
          parentComponent: this,
        },
      },
    );
  }

  applyQueryFilters(params: Params) {
    const { competencia, competencia_final, visao, sort, extraFilters } = params;
    let hasFilter = false;

    if (competencia) {
      hasFilter = true;
      this.competenciaRange[0] = competencia;
    }

    if (competencia_final) {
      hasFilter = true;
      this.competenciaRange[1] = competencia_final;
    }

    if (sort || extraFilters) {
      hasFilter = true;
    }

    const filterModel = this.gridService.getDecodedFilter(extraFilters);
    setTimeout(() => this.gridService.applyVisaoInicial(GRID_KEY, this.agGrid, visao, sort, filterModel));
    setTimeout(() => this.gridService.applyVisaoInicial(GRID_KEY, this.agGridFornecimento, visao, sort, filterModel));

    // Remove filters from url
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });

    this.filterReady$.next(hasFilter);
  }

  reloadData() {
    this.filterChanged = false;

    // Trata o comportamento do componente visual do carregamento
    if (this.firstLoad) {
      this.firstLoad = false;
      this.agGrid?.api?.setGridOption('loading', false);
      this.agGridFornecimento?.api?.setGridOption('loading', false);
      setTimeout(() => {
        this.agGrid?.api?.setGridOption('loading', true);
        this.agGridFornecimento?.api?.setGridOption('loading', true);
      });
    } else {
      this.agGrid?.api?.setGridOption('loading', true);
      this.agGridFornecimento?.api?.setGridOption('loading', true);
    }

    if (!this.competenciaRange[0] || !this.competenciaRange[1]) {
      this.rowData = [];
      this.rowDataFornecimento = [];
      this.agGrid?.api?.setGridOption('pinnedBottomRowData', []);
      return;
    }

    forkJoin([
      this.service.getData(this.operadora, this.competenciaRange[0], this.competenciaRange[1]),
      this.service.getDataFornecimento(this.operadora, this.competenciaRange[0], this.competenciaRange[1]),
    ]).subscribe({
      next: ([row, rowFornecimento]) => {
        if (!row?.length) {
          this.rowData = [];
          this.agGrid?.api?.setGridOption('pinnedBottomRowData', []);
        } else {
          this.rowDataOriginal = row.map(item => {
            return {
              ...item,
              nm_id_sexo: this.geralService.buildNomeDeDominio(item.id_sexo, 'dm_sexo'),
            };
          });
          this.pseudonimizarData();
        }
        if (!rowFornecimento?.length) {
          this.rowDataFornecimento = [];
          this.agGrid?.api?.setGridOption('pinnedBottomRowData', []);
        } else {
          this.rowDataFornecimentoOriginal = rowFornecimento.map(item => {
            return {
              ...item,
              nm_id_sexo: this.geralService.buildNomeDeDominio(item.id_sexo, 'dm_sexo'),
            };
          });
          this.pseudonimizarData();
        }
      },
      error: error => this.notificationService.handleError(error),
      complete: () => {
        this.agGrid?.api?.setGridOption('loading', false);
        this.agGridFornecimento?.api?.setGridOption('loading', false);
      },
    });
  }

  competenciaChanged(range: string[]) {
    this.competenciaRange = range;
    this.store.dispatch(
      OperadorasActions.changeWorkspaceParams({
        partialParams: {
          competencia: +range[0],
          competencia_final: +range[1],
        },
      }),
    );
    this.filterChanged = true;
  }

  toggleFullscreen() {
    this.store.dispatch(LayoutActions.toggleFullscreen({}));
  }

  togglePseudonimizar() {
    this.pseudonimizarAction = !this.pseudonimizarAction;
    this.pseudonimizarData();
  }

  private pseudonimizarData() {
    if (!this.pseudonimizarActionVisible || !this.pseudonimizarAction) {
      this.rowData = this.rowDataOriginal;
      this.rowDataFornecimento = this.rowDataFornecimentoOriginal;
      return;
    }

    this.rowData = this.rowDataOriginal?.map(row => ({
      ...row,
      nr_cns: pseudonimizar(row.nr_cns),
      nr_cpf_beneficiario: pseudonimizar(row.nr_cpf_beneficiario),
    }));

    this.rowDataFornecimento = this.rowDataFornecimentoOriginal?.map(row => ({
      ...row,
      nr_cns: pseudonimizar(row.nr_cns),
      nr_cpf_beneficiario: pseudonimizar(row.nr_cpf_beneficiario),
    }));
  }

  exportExcel(): void {
    const currentGrid = this.tabIndex === 0 ? this.agGrid : this.agGridFornecimento;
    const tabName = this.tabIndex === 0 ? 'GuiasTISS' : 'Fornecimento';

    const fileNamePrefix = `rejeitados-receita-federal-${tabName}-${this.operadoraNome}-${this.competenciaRange[0]}-${this.competenciaRange[1]}`;

    const headers = [
      `Cruzamento de dados do beneficiário na guia com a Receita Federal - ${tabName}`,
      `${this.operadoraNome} - ${this.operadora}`,
      'Competência: ' + AgFilterUtils.competenciaRangeToString(this.competenciaRange),
    ];

    exportGridAsExcel({
      api: currentGrid.api,
      headers,
      fileNamePrefix,
    });
  }

  exportExcelDialog(): void {
    const currentGrid = this.tabIndex === 0 ? this.agGridDialog : this.agGridDialogFornecimento;

    const fileNamePrefix = `lancamentos-rejeitados-receita-federal-${this.operadoraNome}-${this.competenciaRange[0]}-${this.competenciaRange[1]}`;

    // validar texto
    const headers = [
      'Listagem de lançamentos do cruzamento de dados do beneficiário na guia com a Receita Federal',
      `${this.operadoraNome} - ${this.operadora}`,
      'Competência: ' + AgFilterUtils.competenciaRangeToString(this.competenciaRange),
    ];

    exportGridAsExcel({
      api: currentGrid.api,
      headers,
      fileNamePrefix,
    });
  }

  showDetalheGuiaFor(cd_guia_monitor: string) {
    this.detalheGuiaDisplay = true;
    this.detalheGuiaCodigo = cd_guia_monitor;
  }

  openDialogForFornecimentoDireto(row: RowFornecimentoDireto) {
    this.selectedRowFornecimentoDireto = row;
    this.selectedRowIndexFornecimentoDireto = this.rowFornecimentoDireto.indexOf(row);
    this.detalheGuiaCodigoFornecimento = row.cd_guia_monitor;

    this.dialogDisplayFornecimentoDireto = true;
  }

  showDialogFor(rowData: Row | RowFornecimento, index: number) {
    this.selectedRowIndex = index;
    if (this.tabIndex === 0) {
      this.showDialog(rowData as Row);
    } else {
      this.showDialogForFornecimento(rowData as RowFornecimento, index);
    }
  }

  private showDialog(rowData: Row) {
    const { id_sexo, hash_cpf_cns, nr_competencia } = rowData;

    const dt_nascimento = rowData.dt_nascimento ? this.datePipe.transform(rowData.dt_nascimento, 'dd/MM/yyyy') : '';

    if (!this.dialogDisplay) {
      setTimeout(() => this.gridService.applyVisaoInicial(GRID_DIALOG_KEY, this.agGridDialog));
    }
    this.dialogDisplay = true;
    this.dialogDisplayFornecimento = false;

    this.agGridDialog?.api?.setGridOption('loading', true);
    this.service
      .getListagemLancamentos(this.operadora, nr_competencia.toString(), dt_nascimento, id_sexo, hash_cpf_cns)
      .subscribe({
        next: items => {
          this.dialogData = items;
        },
        error: error => this.notificationService.handleError(error),
        complete: () => this.agGridDialog?.api?.setGridOption('loading', false),
      });
  }

  showDialogForFornecimento(rowData: RowFornecimento, index?: number) {
    if (index !== undefined) {
      this.selectedRowIndex = index;
    }

    const { id_sexo, hash_cpf_cns, nr_competencia } = rowData;

    const dt_nascimento = rowData.dt_nascimento ? this.datePipe.transform(rowData.dt_nascimento, 'dd/MM/yyyy') : '';

    if (!this.dialogDisplayFornecimento) {
      setTimeout(() => this.gridService.applyVisaoInicial(GRID_DIALOG_KEY, this.agGridDialogFornecimento));
    }
    this.dialogDisplayFornecimento = true;
    this.dialogDisplay = false;

    this.agGridDialogFornecimento?.api?.setGridOption('loading', true);
    this.service
      .getListagemLancamentosFornecimento(
        this.operadora,
        nr_competencia.toString(),
        dt_nascimento,
        id_sexo,
        hash_cpf_cns,
      )
      .subscribe({
        next: items => {
          this.dialogDataFornecimento = items.map(item => ({
            ...item,
            nm_id_sexo: this.geralService.buildNomeDeDominio(item.id_sexo, 'dm_sexo'),
          }));
        },
        error: error => this.notificationService.handleError(error),
        complete: () => this.agGridDialogFornecimento?.api?.setGridOption('loading', false),
      });
  }

  onDialogMaximize(event) {
    this.dialogHeightPadding = event.maximized ? DIALOG_PADDING_MAX_ON : DIALOG_PADDING_MAX_OFF;
  }

  onDialogHide(event) {
    // console.log('[CpfRepetido] onDialogHide', event);
    // this.router.navigate([this.routeCritica ? '..' : '.'], { relativeTo: this.route });
  }

  canNext(): boolean {
    const currentData = this.tabIndex === 0 ? this.rowData : this.rowDataFornecimento;
    return this.selectedRowIndex < (currentData?.length || 0) - 1;
  }

  canPrev(): boolean {
    return this.selectedRowIndex > 0;
  }

  showDialogForNext() {
    if (this.canNext()) {
      this.selectedRowIndex++;
      const currentData = this.tabIndex === 0 ? this.rowData : this.rowDataFornecimento;
      if (this.tabIndex === 0) {
        this.showDialog(currentData[this.selectedRowIndex] as Row);
      } else {
        this.showDialogForFornecimento(currentData[this.selectedRowIndex] as RowFornecimento, this.selectedRowIndex);
      }
    }
  }

  showDialogForPrev() {
    if (this.canPrev()) {
      this.selectedRowIndex--;
      const currentData = this.tabIndex === 0 ? this.rowData : this.rowDataFornecimento;
      if (this.tabIndex === 0) {
        this.showDialog(currentData[this.selectedRowIndex] as Row);
      } else {
        this.showDialogForFornecimento(currentData[this.selectedRowIndex] as RowFornecimento, this.selectedRowIndex);
      }
    }
  }

  onCopyUrlFilters() {
    const currentGrid = this.tabIndex === 0 ? this.agGrid : this.agGridFornecimento;
    const visao = this.gridService.getCurrentVisao(GRID_KEY);
    const sort = this.gridService.getSimpleSort(this.agGrid);
    // const extraFilters = this.gridService.getEncodedFilter(this.agGrid);
    const extraFilters = this.gridService.getEncodedFilter(currentGrid as AgGridCommon<Row | RowFornecimento, any>);

    const filter = {
      competencia: this.competenciaRange[0],
      competencia_final: this.competenciaRange[1],
      tab: this.tabIndex,
      visao: visao?.cd_visao,
      sort,
      extraFilters,
    };

    const url = filterToQueryString(filter);
    navigator.clipboard.writeText(url);
    this.notificationService.info('Endereço com filtros copiado');
  }

  onTabChange() {
    // console.log('Tab changed to:', this.tabIndex);
  }

  private ngDestroyed$ = new Subject<void>();
  ngOnDestroy() {
    this.ngDestroyed$.next();
    this.ngDestroyed$.complete();
  }
}

export interface Row extends RejeitadosReceitaFederal {
  nm_id_sexo?: string;
}

export interface RowFornecimento extends RejeitadosReceitaFederalFornecimento {
  nm_id_sexo?: string;
}

export interface RowLancamentos extends LancamentosRejeitados {}

export interface RowLancamentosFornecimento extends LancamentosRejeitadosFornecimento {
  nm_id_sexo?: string;
}

export interface RowFornecimentoDireto extends FornecimentoDireto {}
